import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { wallet } from '@cityofzion/neon-js';
import {
  getBalance,
  getBlock,
  readContract,
  switchChain,
  waitForTransactionReceipt,
  writeContract,
} from '@wagmi/core';
import { useWeb3Modal } from '@web3modal/wagmi/react';
import { NeoContext } from 'App';
import { message, Modal, Select, Tooltip } from 'antd';
import BigNumber from 'bignumber.js';
import delay from 'delay';
import { FC, useContext, useEffect, useState } from 'react';
import { decodeEventLog, erc20Abi, formatUnits, parseUnits } from 'viem';
import { useAccount } from 'wagmi';
import { ReactComponent as Arrow } from 'assets/images/arrow.svg';
import { ReactComponent as CloseIcon } from 'assets/images/close.svg';
import confirmIcon from 'assets/images/confirm.svg';
import jump from 'assets/images/jump.svg';
import { ReactComponent as Loading } from 'assets/images/loading.svg';
import { ReactComponent as Logo } from 'assets/images/logo.svg';
import ndmemeIcon from 'assets/images/ndmeme.svg';
import neoIcon from 'assets/images/neo.svg';
import { ReactComponent as TipIcon } from 'assets/images/tip.svg';
import neoxIcon from 'assets/images/x.svg';
import { EvmWalletConnect } from 'components/EvmWalletConnect';
import { HistoryChart } from 'components/HistoryChart';
import { NeoWalletConnect } from 'components/NeoWalletConnect';
import { SUPPORTED_WALLETS_CONFIG } from 'components/NeoWalletConnect/walletsConfig';
import ThemeToggle from 'components/ThemeToggle';
import { neoxBridgeAbi } from 'utils/abis/neox-bridge';
import { GAS_N3_DECIMALS, GAS_NEOX_DECIMALS } from 'utils/constants';
import {
  getDepositIsPaused,
  getN3RegisteredTokens,
  getNeoxRegisteredTokens,
  getNetworkFee,
  getWithdrawIsPaused,
} from 'utils/rpc';
import { formatAddress, post } from 'utils/tools';
import { publicClient, walletConfig } from 'utils/walletConfig';

const N3_GAS_CONTRACT = process.env.REACT_APP_GAS_CONTRACT as string;
const BRIDGE_CONTRACT = process.env.REACT_APP_NEO_BRIDGE_CONTRACT as string;
const NEO_RPC = process.env.REACT_APP_NEO_RPC as string;
const NEOX_CHAIN_ID = process.env.REACT_APP_NEOX_CHAIN_ID as string;
const NEOX_CONTRACT = process.env.REACT_APP_NEOX_BRIDGE_CONTRACT as `0x${string}`;
const NEOX_BLOCK_EXPLORER = process.env.REACT_APP_NEOX_BLOCK_EXPLORER as string;
const NEO_BLOCK_EXPLORER = process.env.REACT_APP_NEO_BLOCK_EXPLORER as string;
const NEO_FURA = process.env.REACT_APP_NEO_FURA as string;
const N3_NETWORK = Number(process.env.REACT_APP_N3_NETWORK ?? 3);
const NEOX_API = process.env.REACT_APP_NEOX_API as string;

export const Index: FC = () => {
  const [messageApi, contextHolder1] = message.useMessage();
  const { open } = useWeb3Modal();
  const { address: neoxAddress, chain: neoxNetwork } = useAccount();
  const {
    connect: connectN3Wallet,
    address: n3Address,
    gasBalance: n3GasBalance,
    network: n3Network,
    getGasBalance: getN3GasBalance,
    dapi: n3Dapi,
    module: n3WalletModule,
  } = useContext(NeoContext);

  const [neoxGasBalance, setNeoxGasBalance] = useState('0');
  const [gasInput, setGasInput] = useState('');
  const [type, setType] = useState('n3ToNeox');
  const [n3InputMax, setN3InputMax] = useState<string | null>(null);
  const [getN3InputMaxPending, setGetN3InputMaxPending] = useState(true);
  const [neoxInputMax, setNeoxInputMax] = useState<string | null>(null);
  const [n3DepositStatus, setN3DepositStatus] = useState('default'); // n3 -> neox
  const [neoxDepositStatus, setNeoxDepositStatus] = useState('default'); // n3 -> neox
  const [n3DepositTxId, setN3DepositTxId] = useState(''); // n3 -> neox
  const [neoxDepositTxId, setNeoxDepositTxId] = useState(''); // n3 -> neox
  const [neoxWithdrawHash, setNeoxWithdrawHash] = useState(''); // neox -> n3
  const [n3WithdrawTxId, setN3WithdrawTxId] = useState(''); // neox -> n3
  const [neoxWithdrawStatus, setNeoxWithdrawStatus] = useState('default'); // neox -> n3
  const [n3WithdrawStatus, setN3WithdrawStatus] = useState('default'); // neox -> n3
  const [n3DepositFee, setN3DepositFee] = useState('0');
  const [contractMaxGasDeposit, setContractMaxGasDeposit] = useState('0');
  const [neoxWithdrawFee, setNeoxWithdrawFee] = useState('0');
  const [contractMaxNeoxDepositFee, setContractMaxNeoxDepositFee] = useState('0');
  const [minDepositAmount, setMinDepositAmount] = useState('0');
  const [maxDepositAmount, setMaxDepositAmount] = useState('0');
  const [minWithdrawAmount, setMinWithdrawAmount] = useState('0');
  const [maxWithdrawAmount, setMaxWithdrawAmount] = useState('0');
  const [limitsLoading, setLimitsLoading] = useState(false);
  const [limitsToken, setLimitsToken] = useState('GAS');
  const [limitsDirection, setLimitsDirection] = useState('n3ToNeox');
  const [selectedToken, setSelecteToken] = useState('GAS');
  const [n3TokenBalance, setN3TokenBalance] = useState('0');
  const [neoxTokenBalance, setNeoxTokenBalance] = useState('0');
  const [isApproving, setIsApproving] = useState(false);
  const [maxTotalDeposit, setMaxTotalDeposit] = useState('0');
  const [totalDeposit, setTotalDeposit] = useState('0');
  const [neoMaxTotalDeposit, setNeoMaxTotalDeposit] = useState<string | null>(null);
  const [neoTotalDeposit, setNeoTotalDeposit] = useState<string | null>(null);
  const [depositIsPaused, setDepositIsPaused] = useState(false);
  const [withdrawIsPaused, setWithdrawIsPaused] = useState(false);
  const [tokenMap, setTokenMap] = useState<any>({
    GAS: {
      neoxContract: NEOX_CONTRACT,
      n3Contract: N3_GAS_CONTRACT,
      abi: neoxBridgeAbi,
      n3Decimals: GAS_N3_DECIMALS,
      neoxDecimals: GAS_NEOX_DECIMALS,
    },
  });

  const [modal, contextHolder] = Modal.useModal();

  type ErrorLike = { message?: string; shortMessage?: string; cause?: { shortMessage?: string } };

  const getErrorMessage = (error: unknown): string => {
    if (typeof error !== 'object' || error == null) {
      return '';
    }
    const typedError = error as ErrorLike;
    return [typedError.shortMessage, typedError.message, typedError.cause?.shortMessage]
      .filter(Boolean)
      .join(' ');
  };

  const getFriendlyWithdrawError = (
    error: unknown,
    tokenSymbol: string,
    tokenDecimals: number,
  ): string | null => {
    const message = getErrorMessage(error);
    if (!message.includes('AmountBelowMinAmount')) {
      return null;
    }

    const match = message.match(/AmountBelowMinAmount[\s\S]*?\((\d+),\s*(\d+)\)/);
    if (!match) {
      return `Withdraw amount must be greater than the minimum required ${tokenSymbol}.`;
    }

    const minAmount = BigNumber(match[1]).shiftedBy(-tokenDecimals).toString();
    return `Withdraw amount must be greater than ${minAmount} ${tokenSymbol}.`;
  };

  useEffect(() => {
    getN3DepositFee();
    getContractMaxGasDeposit();
    getN3MinNativeDeposit();
    getNeoXDepositFee();
    getNativeBridge();
    Promise.all([getN3RegisteredTokens(), getNeoxRegisteredTokens(0, [])]).then((res: any) => {
      const merged = Object.keys({ ...res[0], ...res[1] }).reduce((acc: any, key) => {
        acc[key] = { ...(res[0][key] || {}), ...(res[1][key] || {}) };
        return acc;
      }, {});
      const nextTokenMap = { ...tokenMap, ...merged };
      setTokenMap(nextTokenMap);
      getNeoBridge(nextTokenMap.NEO);
    });
    getDepositIsPaused().then(res => setDepositIsPaused(res));
    getWithdrawIsPaused().then(res => setWithdrawIsPaused(res));
  }, []);

  useEffect(() => {
    updateNeoxBalance(neoxAddress);
  }, [neoxNetwork, neoxAddress, selectedToken, type]);

  useEffect(() => {
    getN3TokenBalance(n3Address);
  }, [n3Network, n3Address]);

  useEffect(() => {
    updateN3Balance(n3Address);
    if (type === 'n3ToNeox') {
      if (selectedToken === 'GAS') {
        getDepositIsPaused().then(res => setDepositIsPaused(res));
      } else {
        getDepositIsPaused(tokenMap[selectedToken].n3Contract).then(res => setDepositIsPaused(res));
      }
    } else {
      if (selectedToken === 'GAS') {
        getWithdrawIsPaused().then(res => setWithdrawIsPaused(res));
      } else {
        getWithdrawIsPaused(tokenMap[selectedToken].neoxContract).then(res =>
          setWithdrawIsPaused(res),
        );
      }
    }
  }, [selectedToken, type]);

  useEffect(() => {
    getNeoxInputMax(selectedToken === 'GAS' ? neoxGasBalance : neoxTokenBalance);
  }, [
    neoxGasBalance,
    neoxWithdrawFee,
    contractMaxNeoxDepositFee,
    selectedToken,
    neoxTokenBalance,
    maxWithdrawAmount,
  ]);

  useEffect(() => {
    getN3InputMax(selectedToken === 'GAS' ? n3GasBalance : n3TokenBalance);
  }, [
    n3GasBalance,
    n3DepositFee,
    contractMaxGasDeposit,
    selectedToken,
    n3TokenBalance,
    maxDepositAmount,
  ]);

  useEffect(() => {
    setGasInput('');
  }, [n3Address, neoxAddress, n3Network, neoxNetwork, type, selectedToken]);

  const showWallets = () => {
    const walletModal = modal.info({
      icon: null,
      width: 400,
      footer: null,
      maskClosable: true,
      content: (
        <div className="w-full pb-[20px]">
          <div className="flex items-center justify-between">
            <div className="font-articulat text-[20px] font-semibold">Connect Wallet</div>
            <div
              className="cursor-pointer rounded-lg p-[8px] duration-200 hover:bg-text/20"
              onClick={() => walletModal.destroy()}
            >
              <CloseIcon className="h-[12px]" />
            </div>
          </div>
          {SUPPORTED_WALLETS_CONFIG.map(config => (
            <div
              key={config.name}
              className={`mt-[20px] flex cursor-pointer items-center gap-[10px] rounded-[10px] border-[1px] border-text/20 bg-card px-[30px] py-[20px] duration-200 hover:bg-text/20 ${
                config.name === 'Neon'
                  ? 'lg:hidden'
                  : config.name === 'OneGate'
                  ? 'hidden lg:flex'
                  : ''
              }`}
              onClick={() => {
                connectN3Wallet(config.name);
                walletModal.destroy();
              }}
            >
              <img src={config.icon} className="h-[24px] w-[24px]" />
              <div className="font-articulat text-[17px] font-semibold">{config.name}</div>
            </div>
          ))}
        </div>
      ),
    });
  };

  const getN3DepositFee = async () => {
    try {
      const body = JSON.stringify({
        params: [BRIDGE_CONTRACT, 'nativeDepositFee'],
        method: 'invokefunction',
        jsonrpc: '2.0',
        id: 1,
      });
      const res = await post(NEO_RPC, body);
      setN3DepositFee(BigNumber(res.result.stack[0].value).shiftedBy(-GAS_N3_DECIMALS).toString());
    } catch (e) {
      console.log(e);
    }
  };

  const getContractMaxGasDeposit = async () => {
    try {
      const body = JSON.stringify({
        params: [BRIDGE_CONTRACT, 'maxNativeDeposit'],
        method: 'invokefunction',
        jsonrpc: '2.0',
        id: 1,
      });
      const res = await post(NEO_RPC, body);
      const max = BigNumber(res.result.stack[0].value).shiftedBy(-GAS_N3_DECIMALS).toString();
      setContractMaxGasDeposit(max);
      setMaxDepositAmount(max);
    } catch (e) {
      console.log(e);
    }
  };

  const getN3MinNativeDeposit = async () => {
    try {
      const body = JSON.stringify({
        params: [BRIDGE_CONTRACT, 'minNativeDeposit'],
        method: 'invokefunction',
        jsonrpc: '2.0',
        id: 1,
      });
      const res = await post(NEO_RPC, body);
      setMinDepositAmount(
        BigNumber(res.result.stack[0].value).shiftedBy(-GAS_N3_DECIMALS).toString(),
      );
    } catch (e) {
      console.log(e);
    }
  };

  const getN3TokenBridgeLimits = async (tokenHash: string) => {
    try {
      const body = JSON.stringify({
        params: [BRIDGE_CONTRACT, 'getTokenBridge', [{ type: 'Hash160', value: tokenHash }]],
        method: 'invokefunction',
        jsonrpc: '2.0',
        id: 1,
      });
      const res = await post(NEO_RPC, body);
      const tokenConfig = res.result.stack[0].value[3];
      const decimals = tokenMap[selectedToken]?.n3Decimals ?? GAS_N3_DECIMALS;
      setMinDepositAmount(BigNumber(tokenConfig.value[2].value).shiftedBy(-decimals).toString());
      setMaxDepositAmount(BigNumber(tokenConfig.value[3].value).shiftedBy(-decimals).toString());
    } catch (e) {
      console.log(e);
    }
  };

  const getNeoXDepositFee = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const result: any = await readContract(walletConfig, {
        abi: neoxBridgeAbi,
        address: NEOX_CONTRACT,
        functionName: 'nativeBridge',
      });
      setNeoxWithdrawFee(formatUnits(result?.[3]?.fee ?? '0', GAS_NEOX_DECIMALS));
      setContractMaxNeoxDepositFee(formatUnits(result?.[3]?.maxAmount ?? '0', GAS_NEOX_DECIMALS));
      setMinWithdrawAmount(formatUnits(result?.[3]?.minAmount ?? '0', GAS_NEOX_DECIMALS));
      setMaxWithdrawAmount(formatUnits(result?.[3]?.maxAmount ?? '0', GAS_NEOX_DECIMALS));
    } catch (e) {
      console.log(e);
    }
  };

  const getN3TokenDepositFee = async (tokenHash: string) => {
    try {
      const body = JSON.stringify({
        params: [BRIDGE_CONTRACT, 'tokenDepositFee', [{ type: 'Hash160', value: tokenHash }]],
        method: 'invokefunction',
        jsonrpc: '2.0',
        id: 1,
      });
      const res = await post(NEO_RPC, body);
      const fee = BigNumber(res.result.stack[0].value).shiftedBy(-GAS_N3_DECIMALS).toString();
      setN3DepositFee(fee);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  const getNeoxTokenWithdrawFee = async (tokenAddress: string) => {
    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const result: any = await readContract(walletConfig, {
        abi: neoxBridgeAbi,
        address: NEOX_CONTRACT,
        functionName: 'tokenBridges',
        args: [tokenAddress],
      });
      const fee = formatUnits(result?.[3]?.fee ?? '0', GAS_NEOX_DECIMALS);
      setNeoxWithdrawFee(fee);
      const tokenDecimals = tokenMap[selectedToken]?.neoxDecimals ?? GAS_NEOX_DECIMALS;
      setMinWithdrawAmount(formatUnits(result?.[3]?.minAmount ?? '0', tokenDecimals));
      setMaxWithdrawAmount(formatUnits(result?.[3]?.maxAmount ?? '0', tokenDecimals));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  // Maximum available funds for GAS on n3
  const getN3InputMax = async (balance: string) => {
    if (!n3Dapi || !n3Address) {
      return;
    }
    if (selectedToken === 'GAS') {
      const effectiveMin = BigNumber(minDepositAmount).isGreaterThan(0) ? minDepositAmount : '1';
      if (BigNumber(balance).minus(n3DepositFee).isLessThan(effectiveMin)) {
        setN3InputMax(balance);
        setGetN3InputMaxPending(false);
        return;
      }
      const max = BigNumber(balance).minus(n3DepositFee).minus(0.1);
      setN3InputMax(
        max.isGreaterThanOrEqualTo(0) ? BigNumber.min(max, contractMaxGasDeposit).toString() : '0',
      );
      setGetN3InputMaxPending(true);
      try {
        const fromScriptHash = n3Dapi.AddressToScriptHash
          ? (await n3Dapi.AddressToScriptHash({ address: n3Address })).scriptHash
          : n3WalletModule?.AddressToScriptHash(n3Address);
        const from = { type: 'Hash160', value: fromScriptHash };
        const to = {
          type: 'Hash160',
          value: neoxAddress ?? '0x0000000000000000000000000000000000000000',
        };
        const amount = {
          type: 'Integer',
          value: BigNumber(2).multipliedBy(100000000).toString(),
        };
        const maxFee = {
          type: 'Integer',
          value: BigNumber(n3DepositFee).multipliedBy(100000000).toString(),
        };
        const signer = {
          account: fromScriptHash,
          scopes: 16,
          allowedContracts: [BRIDGE_CONTRACT, N3_GAS_CONTRACT],
          allowedGroups: [],
        };
        const invokeObj = [BRIDGE_CONTRACT, 'depositNative', [from, to, amount, maxFee], [signer]];
        const fetchInitData = {
          method: 'POST',
          body: JSON.stringify({
            params: invokeObj,
            method: 'invokefunction',
            jsonrpc: '2.0',
            id: 1,
          }),
        };
        const res = await fetch(NEO_RPC, fetchInitData);
        const systemFee = (await res.json())?.result?.gasconsumed;
        const pkRes = n3Dapi.getPublicKey ? await n3Dapi.getPublicKey() : '';
        const networkFee = !pkRes
          ? 0
          : await getNetworkFee(from, to, amount, maxFee, fromScriptHash, pkRes.publicKey);
        const totalFee = BigNumber(systemFee ?? 0)
          .plus(networkFee ?? 0)
          .shiftedBy(-GAS_N3_DECIMALS)
          .plus(0.03);
        const max = BigNumber(balance).minus(totalFee).minus(n3DepositFee);
        setN3InputMax(
          max.isGreaterThanOrEqualTo(0)
            ? BigNumber.min(max, contractMaxGasDeposit).toString()
            : '0',
        );
        setGetN3InputMaxPending(false);
      } catch (err) {
        console.log(err);
        const max = BigNumber(balance).minus(n3DepositFee).minus(0.1);
        setN3InputMax(
          max.isGreaterThanOrEqualTo(0)
            ? BigNumber.min(max, contractMaxGasDeposit).toString()
            : '0',
        );
        setGetN3InputMaxPending(false);
      }
    } else {
      const tokenMax = BigNumber(maxDepositAmount);
      if (tokenMax.isGreaterThan(0) && BigNumber(balance).isGreaterThan(tokenMax)) {
        setN3InputMax(tokenMax.toString());
      } else {
        setN3InputMax(balance);
      }
    }
  };

  const getNeoxGasBalance = async (address?: `0x${string}`) => {
    if (address != null) {
      try {
        const res = await getBalance(walletConfig, {
          address,
        });
        const balance = formatUnits(res.value, res.decimals);
        setNeoxGasBalance(BigNumber(balance).decimalPlaces(GAS_N3_DECIMALS).toString());
      } catch (e) {
        console.log(e);
      }
    } else {
      setNeoxGasBalance('0');
    }
  };

  const getNeoxTokenBalance = async (address?: `0x${string}`) => {
    if (selectedToken !== 'GAS' && address != null) {
      try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const balance: any = await readContract(walletConfig, {
          abi: erc20Abi,
          address: tokenMap[selectedToken].neoxContract,
          functionName: 'balanceOf',
          args: [address],
        });
        setNeoxTokenBalance(
          BigNumber(balance)
            .shiftedBy(-tokenMap[selectedToken].neoxDecimals)
            .decimalPlaces(tokenMap[selectedToken].n3Decimals)
            .toString(),
        );
      } catch (e) {
        console.log(e);
      }
    }
  };

  const updateNeoxBalance = async (address?: `0x${string}`) => {
    getNeoxGasBalance(address);
    getNeoxTokenBalance(address);
  };

  useEffect(() => {
    setLimitsLoading(true);
    const fetchLimits = async () => {
      if (selectedToken === 'GAS') {
        await Promise.all([
          getN3DepositFee(),
          getNeoXDepositFee(),
          getContractMaxGasDeposit(),
          getN3MinNativeDeposit(),
        ]);
      } else if (tokenMap[selectedToken]) {
        const promises: Promise<void>[] = [];
        if (tokenMap[selectedToken].n3Contract) {
          promises.push(getN3TokenDepositFee(tokenMap[selectedToken].n3Contract));
          promises.push(getN3TokenBridgeLimits(tokenMap[selectedToken].n3Contract));
        }
        if (tokenMap[selectedToken].neoxContract) {
          promises.push(getNeoxTokenWithdrawFee(tokenMap[selectedToken].neoxContract));
        }
        await Promise.all(promises);
      }
      setLimitsToken(selectedToken);
      setLimitsDirection(type);
      setLimitsLoading(false);
    };
    fetchLimits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedToken, tokenMap, type]);

  const getN3TokenBalance = async (address?: `0x${string}`) => {
    if (selectedToken !== 'GAS' && address != null) {
      const balance = await n3WalletModule?.getNep17Balance(
        tokenMap[selectedToken].n3Contract,
        address,
      );
      setN3TokenBalance(balance);
    }
  };

  const updateN3Balance = async (address?: `0x${string}`) => {
    getN3GasBalance();
    getN3TokenBalance(address);
  };

  const getNeoxInputMax = async (balance: string) => {
    if (!neoxAddress) {
      return;
    }
    if (selectedToken === 'GAS') {
      const effectiveMin = BigNumber(minWithdrawAmount).isGreaterThan(0) ? minWithdrawAmount : '1';
      if (BigNumber(balance).minus(neoxWithdrawFee).isLessThan(effectiveMin)) {
        setNeoxInputMax(balance);
        return;
      }
      try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const gasUsed = await publicClient.estimateContractGas({
          address: NEOX_CONTRACT,
          abi: neoxBridgeAbi,
          functionName: 'withdrawNative',
          args: [
            `0x${wallet.getScriptHashFromAddress('NNNNNNNNNNNNNNNNNNNNNNNNNNNNNNNNNN')}`,
            parseUnits('0.1', GAS_NEOX_DECIMALS),
          ],
          value: BigInt(1100000000000000000),
          account: neoxAddress,
        });
        const block = await getBlock(walletConfig);
        const baseFeePerGas =
          block.baseFeePerGas != null ? formatUnits(block.baseFeePerGas, GAS_NEOX_DECIMALS) : '0';
        const priorityFee = formatUnits(BigInt(10), GAS_NEOX_DECIMALS / 2);
        const totalGasFee = BigNumber(baseFeePerGas)
          .plus(priorityFee)
          .times(gasUsed.toString())
          .plus(0.001);
        const max = BigNumber(balance).minus(totalGasFee).minus(neoxWithdrawFee);
        setNeoxInputMax(
          max.isGreaterThanOrEqualTo(0)
            ? BigNumber.min(max, contractMaxNeoxDepositFee).toString()
            : '0',
        );
      } catch (e) {
        console.log(e);
        const max = BigNumber(balance).minus(neoxWithdrawFee).minus(0.001);
        setNeoxInputMax(
          max.isGreaterThanOrEqualTo(0)
            ? BigNumber.min(max, contractMaxNeoxDepositFee).toString()
            : '0',
        );
      }
    } else {
      const tokenMax = BigNumber(maxWithdrawAmount);
      if (tokenMax.isGreaterThan(0) && BigNumber(balance).isGreaterThan(tokenMax)) {
        setNeoxInputMax(tokenMax.toString());
      } else {
        setNeoxInputMax(balance);
      }
    }
  };

  const deposit = async () => {
    if (n3Address === '') {
      showWallets();
      return;
    }
    if (n3Network.chainId !== N3_NETWORK) {
      if (n3Dapi.switchWalletNetwork) {
        n3Dapi.switchWalletNetwork({
          chainId: N3_NETWORK,
        });
      } else {
        messageApi.open({
          type: 'error',
          content: `Please switch the wallet network to N3 ${
            N3_NETWORK === 3 ? 'MainNet' : 'TestNet'
          }`,
          style: {
            position: 'relative',
            top: '40px',
          },
        });
      }
      return;
    }
    if (neoxAddress == null) {
      open();
      return;
    }
    const depositMin = BigNumber(minDepositAmount);
    if (depositMin.isGreaterThan(0) && BigNumber(gasInput || 0).isLessThan(depositMin)) {
      messageApi.open({
        type: 'error',
        content: `Deposit amount shouldn't be less than ${depositMin.toString()} ${selectedToken}.`,
        style: {
          position: 'relative',
          top: '40px',
        },
      });
      return;
    }
    if (depositIsPaused) {
      messageApi.open({
        type: 'error',
        content: 'Deposit is paused',
        style: {
          position: 'relative',
          top: '40px',
        },
      });
    } else {
      const confirmModal = modal.confirm({
        icon: null,
        width: 500,
        okText: 'Confirm',
        cancelButtonProps: { style: { display: 'inline-block' } },
        content: (
          <div className="w-full">
            <img src={confirmIcon} className="mx-auto sm:w-[40px]" />
            <div className="mt-[10px] text-center font-articulat text-[30px] font-medium sm:text-[26px]">
              Confirmation
            </div>
            <div className="mt-[30px] text-center sm:mt-[20px] sm:text-[14px]">
              <span className="font-articulat">Are you sure you want to deposit</span>
              <span className="mx-[4px] font-articulat text-primary">
                {BigNumber(gasInput).toString()} {selectedToken}
              </span>
              <span className="font-articulat">to :</span>
            </div>
            <div className="mx-auto mb-[30px] mt-[10px] flex w-full items-center justify-center gap-[8px] rounded-[10px] border-[1px] border-text/20 bg-card py-[15px] text-center font-articulat sm:mb-[20px] sm:py-[10px] sm:text-[10px]">
              <img src={neoxIcon} className="h-[20px] sm:h-[16px]" />
              <span>{neoxAddress}</span>
            </div>
          </div>
        ),
        onOk: () => onConfirm(confirmModal),
      });
    }
  };

  async function onConfirm(confirmModal: any) {
    try {
      confirmModal.update({
        cancelButtonProps: { style: { display: 'none' } },
      });
      const fromScriptHash = n3Dapi.AddressToScriptHash
        ? (await n3Dapi.AddressToScriptHash({ address: n3Address })).scriptHash
        : n3WalletModule?.AddressToScriptHash(n3Address);
      const token = {
        type: 'Hash160',
        value: tokenMap[selectedToken].n3Contract,
      };
      const from = { type: 'Hash160', value: fromScriptHash };
      const to = { type: 'Hash160', value: neoxAddress };
      const amount = {
        type: 'Integer',
        value: BigNumber(gasInput).shiftedBy(tokenMap[selectedToken].n3Decimals).toString(),
      };
      const maxFee = {
        type: 'Integer',
        value: BigNumber(0.1).multipliedBy(100000000).toString(),
      };
      const signer = {
        account: fromScriptHash,
        scopes: 16,
        allowedContracts: [BRIDGE_CONTRACT, N3_GAS_CONTRACT],
        allowedGroups: [],
      };
      const invokeObj: any = {
        scriptHash: BRIDGE_CONTRACT,
        fee: '0.0001',
      };
      if (selectedToken === 'GAS') {
        invokeObj.operation = 'depositNative';
        invokeObj.args = [from, to, amount, maxFee];
      } else {
        invokeObj.operation = 'depositToken';
        invokeObj.args = [token, from, to, amount, maxFee];
        signer.allowedContracts.push(tokenMap[selectedToken].n3Contract);
      }
      setNeoxDepositStatus('default');
      setN3DepositStatus('default');
      setN3DepositTxId('');
      setNeoxDepositTxId('');
      const res = n3Dapi.invoke
        ? await n3Dapi.invoke({ ...invokeObj, signers: [signer] })
        : await n3Dapi.invokeFunction({
            invocations: [invokeObj],
            signers: [signer],
            extraSystemFee: 0,
            extraNetworkFee: 0,
          });
      const txid = res.txid ?? res;
      setN3DepositTxId(txid);
      setN3DepositStatus('pending');
      getApplicationLog(txid);
    } catch (e: any) {
      if (e?.type !== 'CANCELED') {
        setN3DepositStatus('error');
      }
      messageApi.open({
        type: 'error',
        content: e?.description ?? e?.message,
        style: {
          position: 'relative',
          top: '40px',
        },
      });
    }
  }

  // 轮训获取日志
  async function getApplicationLog(txid: string) {
    let status = 0;
    let executions: any;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const body = JSON.stringify({
        params: [txid],
        method: 'getapplicationlog',
        jsonrpc: '2.0',
        id: 1,
      });
      try {
        const res = await post(NEO_RPC, body);
        if (res?.result?.executions?.[0]?.vmstate === 'HALT') {
          status = 1;
          executions = res?.result?.executions;
        } else if (res?.result?.executions?.[0]?.vmstate === 'FAULT') {
          status = -1;
          executions = res?.result?.executions;
        }
      } catch (e) {
        console.log(e);
        status = 0;
      }
      if (!status) {
        await delay(5000); // n3 version acceleration
        continue;
      }
      break;
    }
    if (status !== 0) {
      if (executions != null && executions?.[0]?.exception === null && status === 1) {
        setN3DepositStatus('success');
        const nonce =
          selectedToken === 'GAS'
            ? executions[0].notifications?.find((v: any) => v.eventname === 'NativeDeposit').state
                .value[0].value
            : executions[0].notifications?.find((v: any) => v.eventname === 'TokenDeposit').state
                .value[2].value;
        getMinted(Number(nonce));
      } else {
        setN3DepositStatus('error');
      }
      setGasInput('');
      updateN3Balance(n3Address);
      refreshBridgeStatus();
    }
  }

  function getMinted(nonce: number) {
    const EmptyHash = '0x' + '00'.repeat(32);
    setNeoxDepositStatus('pending');
    let isFetching = false;
    const timer = setInterval(async () => {
      if (isFetching) {
        return;
      }
      try {
        isFetching = true;
        const fetchUrl =
          selectedToken === 'GAS'
            ? `${NEOX_API}/api/v1/transactions/deposits/${nonce}`
            : `${NEOX_API}/api/v1/transactions/deposits/${tokenMap[selectedToken].neoxContract}/${nonce}`;
        const txid = (await fetch(fetchUrl).then(v => v.json()))?.txid;
        isFetching = false;
        if (txid != null && txid != EmptyHash) {
          setNeoxDepositTxId(txid);
          clearInterval(timer);
          setNeoxDepositStatus('success');
          updateNeoxBalance(neoxAddress);
          refreshBridgeStatus();
          clearTimeout(timer2);
        }
      } catch (e: any) {
        clearTimeout(timer2);
        setNeoxDepositStatus('error');
        modal.error({
          title: 'Error',
          content: <div className="text-center">Can't get minted: {e.message}</div>,
        });
        clearInterval(timer);
      }
    }, 10000);
    const timer2 = setTimeout(() => {
      refreshBridgeStatus();
      clearInterval(timer);
      setNeoxDepositStatus('error');
      modal.error({
        title: 'Error',
        content: (
          <div>
            Oops! The request for your Deposit result has timed out, but no worries—your funds are
            completely safe. Please wait a bit and check your wallet later.
          </div>
        ),
      });
    }, 180000);
  }

  const withdraw = async () => {
    if (neoxAddress == null) {
      open();
      return;
    }
    if (neoxNetwork?.id !== parseInt(NEOX_CHAIN_ID)) {
      switchChain(walletConfig, { chainId: parseInt(NEOX_CHAIN_ID) });
      return;
    }
    if (n3Address === '') {
      showWallets();
      return;
    }
    const withdrawMin = BigNumber(minWithdrawAmount);
    if (withdrawMin.isGreaterThan(0) && BigNumber(gasInput || 0).isLessThan(withdrawMin)) {
      messageApi.open({
        type: 'error',
        content: `Withdraw amount shouldn't be less than ${withdrawMin.toString()} ${selectedToken}.`,
        style: {
          position: 'relative',
          top: '40px',
        },
      });
      return;
    }
    if (withdrawIsPaused) {
      messageApi.open({
        type: 'error',
        content: 'Withdraw is paused',
        style: {
          position: 'relative',
          top: '40px',
        },
      });
    } else {
      const confirmModal = modal.confirm({
        icon: null,
        width: 500,
        okText: 'Confirm',
        cancelButtonProps: { style: { display: 'inline-block' } },
        content: (
          <div className="w-full">
            <img src={confirmIcon} className="mx-auto sm:w-[40px]" />
            <div className="mt-[10px] text-center font-articulat text-[30px] font-medium sm:text-[26px]">
              Confirmation
            </div>
            <div className="mt-[30px] text-center sm:mt-[20px] sm:text-[14px]">
              <span className="font-articulat">Are you sure you want to withdraw</span>
              <span className="mx-[4px] font-articulat text-primary">
                {BigNumber(gasInput).toString()} {selectedToken}
              </span>
              <span className="font-articulat">to :</span>
            </div>
            <div className="mx-auto mb-[30px] mt-[10px] flex w-full items-center justify-center gap-[8px] rounded-[10px] border-[1px] border-text/20 bg-card py-[15px] text-center font-articulat sm:mb-[20px] sm:py-[10px] sm:text-[12px]">
              <img src={neoIcon} className="h-[20px] sm:h-[16px]" />
              <span>{n3Address}</span>
            </div>
          </div>
        ),
        onOk: () => confirmWithdraw(confirmModal),
      });
    }
  };

  const confirmWithdraw = async (confirmModal: any) => {
    if (neoxAddress != null) {
      try {
        confirmModal.update({
          cancelButtonProps: { style: { display: 'none' } },
        });
        setNeoxWithdrawHash('');
        setN3WithdrawTxId('');
        setN3WithdrawStatus('default');
        setNeoxWithdrawStatus('default');
        let couldWithdraw = true;
        let params: any = {
          abi: neoxBridgeAbi,
          address: NEOX_CONTRACT,
        };
        if (selectedToken === 'GAS') {
          params = {
            ...params,
            functionName: 'withdrawNative',
            args: [
              `0x${wallet.getScriptHashFromAddress(n3Address)}`,
              parseUnits('0.1', GAS_NEOX_DECIMALS),
            ],
            value: parseUnits(gasInput, tokenMap[selectedToken].neoxDecimals),
          };
        } else {
          params = {
            ...params,
            functionName: 'withdrawToken',
            args: [
              tokenMap[selectedToken].neoxContract,
              `0x${wallet.getScriptHashFromAddress(n3Address)}`,
              parseUnits(gasInput, tokenMap[selectedToken].neoxDecimals),
            ],
            value: parseUnits('0.1', GAS_NEOX_DECIMALS),
          };
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const allowanceAmount: any = await readContract(walletConfig, {
            abi: erc20Abi,
            address: tokenMap[selectedToken].neoxContract,
            functionName: 'allowance',
            args: [neoxAddress, NEOX_CONTRACT],
          });
          if (
            BigNumber(
              formatUnits(allowanceAmount, tokenMap[selectedToken].neoxDecimals),
            ).isLessThan(gasInput)
          ) {
            couldWithdraw = false;
            setIsApproving(true);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const approveHash = await writeContract(walletConfig, {
              abi: erc20Abi,
              address: tokenMap[selectedToken].neoxContract,
              functionName: 'approve',
              args: [NEOX_CONTRACT, parseUnits(gasInput, tokenMap[selectedToken].neoxDecimals)],
            });
            const transactionReceipt = await waitForTransactionReceipt(walletConfig, {
              hash: approveHash,
              pollingInterval: 5_000,
            });
            setIsApproving(false);
            if (transactionReceipt.status === 'success') {
              confirmWithdraw(confirmModal);
            }
          }
        }
        if (couldWithdraw) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const withdrawHash = await writeContract(walletConfig, params);
          setNeoxWithdrawHash(withdrawHash);
          setNeoxWithdrawStatus('pending');
          getWithdrawResult(withdrawHash);
        }
      } catch (err: any) {
        setIsApproving(false);
        if (err.message.indexOf('User rejected') >= 0) {
          messageApi.open({
            type: 'error',
            content: 'User rejected the request.',
            style: {
              position: 'relative',
              top: '40px',
            },
          });
        } else {
          const friendlyMessage = getFriendlyWithdrawError(
            err,
            selectedToken,
            tokenMap[selectedToken].neoxDecimals,
          );
          modal.error({
            title: 'Error',
            content: <div className="text-center">{friendlyMessage ?? err.message}</div>,
          });
        }
      }
    }
  };

  const getWithdrawResult = async (withdrawHash: `0x${string}`) => {
    try {
      const withdrawResult = await waitForTransactionReceipt(walletConfig, {
        hash: withdrawHash,
        pollingInterval: 5_000,
      });
      if (withdrawResult.status === 'success') {
        setNeoxWithdrawStatus('success');
        setN3WithdrawStatus('pending');
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const res: any = decodeEventLog({
          abi: neoxBridgeAbi,
          data: withdrawResult.logs[withdrawResult.logs.length - 1].data,
          topics: withdrawResult.logs[withdrawResult.logs.length - 1].topics,
          strict: false,
        });
        const nonce = res?.args?.nonce;
        let isFetching = false;
        const timer = setInterval(async () => {
          try {
            if (isFetching) {
              return;
            }
            isFetching = true;
            const res = await post(
              NEO_FURA,
              JSON.stringify({
                jsonrpc: '2.0',
                method: 'GetBridgeTxByNonce',
                params: {
                  ContractHash: BRIDGE_CONTRACT,
                  TokenHash: selectedToken === 'GAS' ? '' : tokenMap[selectedToken].n3Contract,
                  Nonce: Number(nonce),
                },
                id: 1,
              }),
            );
            isFetching = false;
            if (res.result != null) {
              if (res.result.Vmstate === 'HALT') {
                setN3WithdrawStatus('success');
              } else {
                setN3WithdrawStatus('error');
              }
              setN3WithdrawTxId(res.result.txid);
              clearInterval(timer);
              setGasInput('');
              updateNeoxBalance(neoxAddress);
              updateN3Balance(n3Address);
              refreshBridgeStatus();
              clearTimeout(timer2);
            }
          } catch (e: any) {
            clearTimeout(timer2);
            refreshBridgeStatus();
            setN3WithdrawStatus('error');
            modal.error({
              title: 'Error',
              content: <div className="text-center">Can't get withdrawn: {e.message}</div>,
            });
            clearInterval(timer);
          }
        }, 10000);
        const timer2 = setTimeout(() => {
          refreshBridgeStatus();
          clearInterval(timer);
          setN3WithdrawStatus('error');
          modal.error({
            title: 'Error',
            content: (
              <div>
                Oops! The request for your Withdraw result has timed out, but no worries—your funds
                are completely safe. Please wait a bit and check your wallet later.
              </div>
            ),
          });
        }, 180000);
      } else {
        setNeoxWithdrawStatus('error');
      }
    } catch (err) {
      setNeoxWithdrawStatus('error');
    }
  };

  const getNativeBridge = async () => {
    try {
      const body = JSON.stringify({
        params: [BRIDGE_CONTRACT, 'getNativeBridge'],
        method: 'invokefunction',
        jsonrpc: '2.0',
        id: 1,
      });
      const res = await post(NEO_RPC, body);
      setMaxTotalDeposit(
        BigNumber(res.result.stack[0].value[4].value[4].value)
          .shiftedBy(-GAS_N3_DECIMALS)
          .toString(),
      );
      setTotalDeposit(
        BigNumber(res.result.stack[0].value[1].value).shiftedBy(-GAS_N3_DECIMALS).toString(),
      );
    } catch (e) {
      console.log(e);
    }
  };

  const limitsReady = !limitsLoading && limitsToken === selectedToken && limitsDirection === type;
  const currentMin = type === 'n3ToNeox' ? minDepositAmount : minWithdrawAmount;
  const currentMax = type === 'n3ToNeox' ? maxDepositAmount : maxWithdrawAmount;
  const inputAmount = BigNumber(gasInput || 0);
  const isAmountEmpty = gasInput === '' || inputAmount.isLessThanOrEqualTo(0);
  const hasAmountError =
    !isAmountEmpty &&
    limitsReady &&
    ((BigNumber(currentMin).isGreaterThan(0) && inputAmount.isLessThan(currentMin)) ||
      (BigNumber(currentMax).isGreaterThan(0) && inputAmount.isGreaterThan(currentMax)));
  const isSubmitBlocked = isAmountEmpty || hasAmountError || !limitsReady;
  const getNeoBridge = async (token?: { neoxContract?: `0x${string}`; neoxDecimals?: number }) => {
    if (!token?.neoxContract) {
      setNeoMaxTotalDeposit(null);
      setNeoTotalDeposit(null);
      return;
    }
    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const [balance, totalSupply]: any = await Promise.all([
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        readContract(walletConfig, {
          abi: erc20Abi,
          address: token.neoxContract,
          functionName: 'balanceOf',
          args: [NEOX_CONTRACT],
        }),
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        readContract(walletConfig, {
          abi: erc20Abi,
          address: token.neoxContract,
          functionName: 'totalSupply',
        }),
      ]);
      const decimals: any = token.neoxDecimals ?? 0;
      const balanceBigInt = BigInt(balance);
      const totalSupplyBigInt = BigInt(totalSupply);
      setNeoTotalDeposit(
        BigNumber(formatUnits(totalSupplyBigInt - balanceBigInt, decimals)).toString(),
      );
      setNeoMaxTotalDeposit(BigNumber(formatUnits(totalSupplyBigInt, decimals)).toString());
    } catch (e) {
      console.log(e);
      setNeoMaxTotalDeposit(null);
      setNeoTotalDeposit(null);
    }
  };

  const refreshBridgeStatus = () => {
    getNativeBridge();
    getNeoBridge(tokenMap.NEO);
  };

  const bridgeFootnote =
    'Please note that the Neo X mainnet bridging service has now entered a stable ' +
    'operation phase. Both GAS and NEO bidirectional bridging services are fully ' +
    'available. As the bridging service continues to operate stably, we will further ' +
    'increase the quotas based on actual conditions and gradually expand bridging ' +
    'support to more assets, delivering a smoother experience for users.';

  return (
    <div className="relative sm:px-[16px]">
      {contextHolder}
      {contextHolder1}
      <div className="z-1 relative mt-[20px] flex items-center justify-between sm:mt-[14px]">
        <div className="ml-[110px] shrink-0 sm:ml-[10px]">
          <Logo className="h-[35px] text-text sm:h-[25px]" />
        </div>
        <div className="mr-[25px] flex items-center gap-[25px] sm:mr-[0px] sm:gap-[12px]">
          <div className="flex items-center gap-[25px] font-semibold sm:hidden">
            <div className="cursor-pointer text-primary">Bridge</div>
            <div
              className="cursor-pointer duration-200 hover:text-primary"
              onClick={() =>
                window.open(
                  N3_NETWORK === 6
                    ? 'https://testnet.governance.banelabs.org/'
                    : 'https://xgovernance.neo.org/',
                  '_blank',
                )
              }
            >
              Vote
            </div>
            <div
              className="cursor-pointer duration-200 hover:text-primary"
              onClick={() =>
                window.open(
                  N3_NETWORK === 6
                    ? 'https://neoxt4scan.ngd.network/'
                    : 'https://neoxscan.ngd.network/',
                  '_blank',
                )
              }
            >
              Explorer
            </div>
          </div>
          <NeoWalletConnect className="h-[37px] w-[154px] rounded-[50px] border-[1px] border-white/20 bg-primary text-[14px] font-semibold text-white shadow-box-shadow duration-200 hover:bg-primary/75 sm:w-[98px] sm:text-[9px]" />
          <EvmWalletConnect className="h-[37px] w-[154px] rounded-[50px] border-[1px] border-white/20 bg-secondary text-[14px] font-semibold text-white shadow-box-shadow duration-200 hover:bg-secondary/75 sm:w-[98px] sm:text-[9px]" />
          <ThemeToggle />
        </div>
      </div>
      <div className="mt-[52px] text-center text-[40px] font-bold sm:mt-[32px] sm:text-[34px]">
        Bridge
      </div>
      {N3_NETWORK === 3 && (
        <div className="z-1 relative mx-auto mt-[16px] w-[600px] rounded-[20px] bg-orange2 px-[28px] py-[16px] sm:w-full sm:px-[16px]">
          <div className="text-[22px] font-semibold text-orange">Footnote</div>
          <div className="mt-[8px] text-[12px] text-orange">{bridgeFootnote}</div>
          <div className="mt-[8px] flex items-center justify-between">
            <div className="text-[14px] font-medium">Bridged GAS</div>
            <div className="text-[14px] font-medium">
              {BigNumber(totalDeposit).toFormat(0)} / {BigNumber(maxTotalDeposit).toFormat(0)}
            </div>
          </div>
          <div className="mt-[8px] h-[8px] w-full bg-white/30">
            <div
              className={`h-full bg-orange`}
              style={{
                width: `${
                  maxTotalDeposit !== '0'
                    ? BigNumber(totalDeposit)
                        .div(maxTotalDeposit)
                        .multipliedBy(100)
                        .toFixed(0)
                        .toString()
                    : '0'
                }%`,
              }}
            />
          </div>
          <div className="mt-[8px] text-right text-[14px]">
            {maxTotalDeposit !== '0'
              ? BigNumber(totalDeposit).div(maxTotalDeposit).multipliedBy(100).toFixed(2).toString()
              : '0'}
            %
          </div>
          {neoTotalDeposit !== null && neoMaxTotalDeposit !== null && (
            <>
              <div className="mt-[12px] flex items-center justify-between">
                <div className="text-[14px] font-medium">Bridged NEO</div>
                <div className="text-[14px] font-medium">
                  {BigNumber(neoTotalDeposit).toFormat(0)} /{' '}
                  {BigNumber(neoMaxTotalDeposit).toFormat(0)}
                </div>
              </div>
              <div className="mt-[8px] h-[8px] w-full bg-white/30">
                <div
                  className="h-full bg-orange"
                  style={{
                    width: `${
                      neoMaxTotalDeposit !== '0'
                        ? BigNumber(neoTotalDeposit)
                            .div(neoMaxTotalDeposit)
                            .multipliedBy(100)
                            .toFixed(0)
                            .toString()
                        : '0'
                    }%`,
                  }}
                />
              </div>
              <div className="mt-[8px] text-right text-[14px]">
                {neoMaxTotalDeposit !== '0'
                  ? BigNumber(neoTotalDeposit)
                      .div(neoMaxTotalDeposit)
                      .multipliedBy(100)
                      .toFixed(2)
                      .toString()
                  : '0'}
                %
              </div>
            </>
          )}
        </div>
      )}

      <div className="z-1 relative mx-auto mt-[18px] w-[600px] rounded-[20px] border-[1px] border-text/20 bg-card p-[28px] sm:w-full">
        <div className="mb-[20px] flex items-center gap-[10px]">
          <div className="text-[20px] font-bold">Token</div>
          <Select
            value={selectedToken}
            style={{ width: 140, height: 36 }}
            onChange={v => setSelecteToken(v)}
            options={Object.keys(tokenMap).map(v => {
              return {
                value: v,
                label: (
                  <div className="flex items-center gap-[6px]">
                    <div className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full bg-text">
                      <img
                        src={
                          v === 'NDMEME'
                            ? ndmemeIcon
                            : `https://neo.org/images/gui/${tokenMap[v].n3Contract}.png`
                        }
                        className={v === 'NDMEME' ? 'h-[24px] w-[24px]' : 'w-[12px]'}
                        onError={e => {
                          if (e.currentTarget.src !== 'https://neo.org/images/gui/default.png') {
                            e.currentTarget.src = 'https://neo.org/images/gui/default.png';
                          }
                        }}
                      />
                    </div>
                    <span className="shrink-0 font-bold leading-none text-text">{v}</span>
                  </div>
                ),
              };
            })}
          />
        </div>
        <div className="flex min-h-[80px] items-center gap-[10px] rounded-[10px] border-[1px] border-text/20 bg-card px-[16px] py-[12px]">
          <div className="shrink-0">
            <div className="mb-[2px] font-semibold text-muted sm:text-[14px]">From</div>
            <div className="flex items-center justify-center gap-[10px] rounded-[30px] border-[1px] border-text/20 bg-card px-[8px] py-[5px] sm:px-[5px]">
              <div className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full bg-text p-[5px] sm:h-[18px] sm:w-[18px]">
                <img className="w-full" src={type === 'n3ToNeox' ? neoIcon : neoxIcon} />
              </div>
              <span className="shrink-0 font-bold leading-none sm:text-[14px]">
                {type === 'n3ToNeox' ? 'Neo N3' : 'Neo X'}
              </span>
            </div>
          </div>
          {type === 'n3ToNeox' && (
            <>
              {n3Address !== '' && (
                <div className="ml-auto border-r-[1px] border-text/20 pr-[10px] text-right sm:border-0 sm:pr-0">
                  {n3Address !== '' && (
                    <button
                      className={`ml-auto hidden border-l-[1px] border-text/20 text-[20px] font-bold text-primary sm:block sm:border-0 sm:text-[18px] ${
                        getN3InputMaxPending && 'cursor-not-allowed'
                      }`}
                      onClick={
                        getN3InputMaxPending
                          ? undefined
                          : () =>
                              setGasInput(
                                n3InputMax
                                  ? BigNumber(n3InputMax)
                                      .decimalPlaces(tokenMap[selectedToken].n3Decimals)
                                      .toString()
                                  : selectedToken !== 'GAS'
                                  ? BigNumber(n3TokenBalance)
                                      .decimalPlaces(tokenMap[selectedToken].n3Decimals)
                                      .toString()
                                  : BigNumber(n3GasBalance)
                                      .minus(n3DepositFee)
                                      .minus(0.1)
                                      .decimalPlaces(tokenMap[selectedToken].n3Decimals)
                                      .toString(),
                              )
                      }
                    >
                      MAX
                    </button>
                  )}
                  <input
                    className="mb-[2px] max-w-[300px] bg-transparent text-right text-[24px] font-medium outline-none sm:max-w-[120px] sm:text-[16px]"
                    placeholder="Enter amount"
                    value={gasInput}
                    onInput={e => {
                      let value = e.currentTarget.value;
                      if (tokenMap[selectedToken].n3Decimals === 0) {
                        value = value.replace(/[^\d]/g, '');
                      } else {
                        value = value.replace(/[^\d.]/g, '').replace(/(\.\d+)\.+/g, '$1');
                      }
                      if (value === '') {
                        setGasInput('');
                        return;
                      }
                      if (e.currentTarget.value[0] === '.') {
                        value = `0${value}`;
                      }
                      if (
                        e.currentTarget.value.indexOf('.') >= 0 &&
                        e.currentTarget.value.split('.')[1].length >
                          tokenMap[selectedToken].n3Decimals
                      ) {
                        value = e.currentTarget.value.slice(0, -1);
                      }
                      if (
                        n3InputMax != null &&
                        BigNumber(value).isGreaterThanOrEqualTo(n3InputMax)
                      ) {
                        value = BigNumber(n3InputMax)
                          .decimalPlaces(tokenMap[selectedToken].n3Decimals)
                          .toString();
                      } else if (
                        BigNumber(value).isGreaterThanOrEqualTo(n3GasBalance) &&
                        selectedToken === 'GAS'
                      ) {
                        value = BigNumber(n3GasBalance)
                          .minus(n3DepositFee)
                          .minus(0.1)
                          .decimalPlaces(tokenMap[selectedToken].n3Decimals)
                          .toString();
                      }
                      setGasInput(value);
                    }}
                  />
                  <div className="text-right text-[12px] font-medium text-text">
                    Balance{' '}
                    <span className="font-bold">
                      {BigNumber(
                        selectedToken === 'GAS' ? n3GasBalance : n3TokenBalance,
                      ).toString()}
                    </span>
                  </div>
                </div>
              )}
              {n3Address !== '' && (
                <button
                  className={`text-[20px] font-bold text-primary sm:hidden ${
                    getN3InputMaxPending && 'cursor-not-allowed'
                  }`}
                  onClick={
                    getN3InputMaxPending
                      ? undefined
                      : () =>
                          setGasInput(
                            n3InputMax
                              ? BigNumber(n3InputMax)
                                  .decimalPlaces(tokenMap[selectedToken].n3Decimals)
                                  .toString()
                              : selectedToken !== 'GAS'
                              ? BigNumber(n3TokenBalance)
                                  .decimalPlaces(tokenMap[selectedToken].n3Decimals)
                                  .toString()
                              : BigNumber(n3GasBalance)
                                  .minus(n3DepositFee)
                                  .minus(0.1)
                                  .decimalPlaces(tokenMap[selectedToken].n3Decimals)
                                  .toString(),
                          )
                  }
                >
                  MAX
                </button>
              )}
              {n3Address === '' && (
                <NeoWalletConnect className="ml-auto h-[37px] w-[154px] rounded-[50px] border-[1px] border-white/20 bg-primary text-[14px] font-semibold text-white shadow-box-shadow duration-200 hover:bg-primary/75 sm:w-[98px] sm:text-[9px]" />
              )}
            </>
          )}
          {type === 'neoxToN3' && (
            <>
              {neoxAddress != null && (
                <div className="ml-auto border-r-[1px] border-text/20 pr-[10px] text-right sm:border-0 sm:pr-0">
                  {neoxAddress != null && (
                    <button
                      className="ml-auto hidden text-[20px] font-bold text-primary sm:block sm:text-[18px]"
                      onClick={() =>
                        setGasInput(
                          neoxInputMax
                            ? BigNumber(neoxInputMax)
                                .decimalPlaces(tokenMap[selectedToken].n3Decimals)
                                .toString()
                            : selectedToken !== 'GAS'
                            ? BigNumber(neoxTokenBalance)
                                .decimalPlaces(tokenMap[selectedToken].n3Decimals)
                                .toString()
                            : BigNumber(neoxGasBalance)
                                .minus(neoxWithdrawFee)
                                .minus(0.1)
                                .decimalPlaces(tokenMap[selectedToken].n3Decimals)
                                .toString(),
                        )
                      }
                    >
                      MAX
                    </button>
                  )}
                  <input
                    className="mb-[2px] max-w-[300px] bg-transparent text-right text-[24px] font-medium outline-none sm:max-w-[120px] sm:text-[16px]"
                    placeholder="Enter amount"
                    value={gasInput}
                    onInput={e => {
                      let value = e.currentTarget.value;
                      if (tokenMap[selectedToken].n3Decimals === 0) {
                        value = value.replace(/[^\d]/g, '');
                      } else {
                        value = value.replace(/[^\d.]/g, '').replace(/(\.\d+)\.+/g, '$1');
                      }
                      if (value === '') {
                        setGasInput('');
                        return;
                      }
                      if (e.currentTarget.value[0] === '.') {
                        value = `0${value}`;
                      }
                      if (
                        e.currentTarget.value.indexOf('.') >= 0 &&
                        e.currentTarget.value.split('.')[1].length >
                          tokenMap[selectedToken].n3Decimals
                      ) {
                        value = e.currentTarget.value.slice(0, -1);
                      }
                      if (
                        neoxInputMax != null &&
                        BigNumber(value).isGreaterThanOrEqualTo(neoxInputMax)
                      ) {
                        value = BigNumber(neoxInputMax)
                          .decimalPlaces(tokenMap[selectedToken].n3Decimals)
                          .toString();
                      } else if (
                        BigNumber(value).isGreaterThanOrEqualTo(neoxGasBalance) &&
                        selectedToken === 'GAS'
                      ) {
                        value = BigNumber(neoxGasBalance)
                          .minus(1)
                          .decimalPlaces(tokenMap[selectedToken].n3Decimals)
                          .toString();
                      }
                      setGasInput(value);
                    }}
                  />
                  <div className="text-right text-[12px] font-medium text-text">
                    Balance{' '}
                    <span className="font-bold">
                      {BigNumber(
                        selectedToken === 'GAS' ? neoxGasBalance : neoxTokenBalance,
                      ).toString()}
                    </span>
                  </div>
                </div>
              )}
              {neoxAddress != null && (
                <button
                  className="text-[20px] font-bold text-primary sm:hidden"
                  onClick={() =>
                    setGasInput(
                      neoxInputMax
                        ? BigNumber(neoxInputMax)
                            .decimalPlaces(tokenMap[selectedToken].n3Decimals)
                            .toString()
                        : selectedToken !== 'GAS'
                        ? BigNumber(neoxTokenBalance)
                            .decimalPlaces(tokenMap[selectedToken].n3Decimals)
                            .toString()
                        : BigNumber(neoxGasBalance)
                            .minus(neoxWithdrawFee)
                            .minus(0.1)
                            .decimalPlaces(tokenMap[selectedToken].n3Decimals)
                            .toString(),
                    )
                  }
                >
                  MAX
                </button>
              )}
              {neoxAddress == null && (
                <EvmWalletConnect className="ml-auto h-[37px] w-[154px] rounded-[50px] border-[1px] border-white/20 bg-secondary text-[14px] font-semibold text-white shadow-box-shadow duration-200 hover:bg-secondary/75 sm:w-[98px] sm:text-[9px]" />
              )}
            </>
          )}
        </div>
        <div
          className="relative mx-auto my-[7px] flex h-[1px]"
          onClick={() => setType(type === 'n3ToNeox' ? 'neoxToN3' : 'n3ToNeox')}
        >
          <div className="absolute left-1/2 top-1/2 h-[62px] w-[62px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[1px] border-text/20 bg-card p-[8px] sm:h-[45px] sm:w-[45px]">
            <div className="flex h-full w-full cursor-pointer items-center justify-center rounded-full bg-primary text-white shadow-box-shadow duration-200 hover:bg-primary/75">
              <Arrow className="sm:h-[12px]" />
            </div>
          </div>
        </div>
        <div className="flex h-[80px] items-center gap-[10px] rounded-[10px] border-[1px] border-text/20 bg-card px-[16px] py-[12px]">
          <div className="shrink-0">
            <div className="mb-[2px] font-semibold text-muted sm:text-[14px]">To</div>
            <div className="flex items-center justify-center gap-[10px] rounded-[30px] border-[1px] border-text/20 bg-card px-[8px] py-[5px] sm:px-[5px]">
              <div className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full bg-text p-[5px] sm:h-[18px] sm:w-[18px]">
                <img className="w-full" src={type === 'n3ToNeox' ? neoxIcon : neoIcon} />
              </div>
              <span className="shrink-0 font-bold leading-none sm:text-[14px]">
                {type === 'n3ToNeox' ? 'Neo X' : 'Neo N3'}
              </span>
            </div>
          </div>
          {type === 'n3ToNeox' && (
            <div className="ml-auto text-right text-[14px] font-medium text-text">
              {neoxAddress ? (
                <>
                  <span className="sm:hidden">{neoxAddress}</span>
                  <span className="hidden sm:inline">{formatAddress(neoxAddress)}</span>
                </>
              ) : (
                <EvmWalletConnect className="ml-auto h-[37px] w-[154px] rounded-[50px] border-[1px] border-white/20 bg-secondary text-[14px] font-semibold text-white shadow-box-shadow duration-200 hover:bg-secondary/75 sm:w-[98px] sm:text-[9px]" />
              )}
            </div>
          )}
          {type === 'neoxToN3' && (
            <div className="ml-auto text-right text-[14px] font-medium text-text">
              {n3Address ? (
                <>
                  <span className="sm:hidden">{n3Address}</span>
                  <span className="hidden sm:inline">{formatAddress(n3Address)}</span>
                </>
              ) : (
                <NeoWalletConnect className="ml-auto h-[37px] w-[154px] rounded-[50px] border-[1px] border-white/20 bg-primary text-[14px] font-semibold text-white shadow-box-shadow duration-200 hover:bg-primary/75 sm:w-[98px] sm:text-[9px]" />
              )}
            </div>
          )}
        </div>
        <div className="mt-[18px] px-[20px] text-[14px] font-medium sm:px-0 sm:text-[12px]">
          <div className="flex items-center gap-[5px] border-b-[1px] border-text/20 pb-[8px]">
            <div>Bridge Fee</div>
            <Tooltip
              title={`The bridge contract charges ${
                type === 'n3ToNeox' ? n3DepositFee : neoxWithdrawFee
              } GAS for bridging ${selectedToken}. The platform does not charge any other fees.`}
              placement="top"
              color="hsl(var(--primary))"
              overlayInnerStyle={{
                color: 'black',
                padding: '20px 14px',
                marginLeft: '10px',
                width: '320px',
              }}
            >
              <TipIcon />
            </Tooltip>
            <div className="ml-auto">
              {type === 'n3ToNeox' ? n3DepositFee : neoxWithdrawFee} GAS
            </div>
          </div>
          <div className="mt-[8px] flex items-center justify-between">
            <div>Actual Receive</div>
            {selectedToken === 'GAS' ? (
              <div className="text-primary">
                {!isAmountEmpty && !hasAmountError ? BigNumber(gasInput).toString() : '-'} GAS
              </div>
            ) : (
              <div className="text-primary">
                {!isAmountEmpty && !hasAmountError ? gasInput : '-'} {selectedToken}
              </div>
            )}
          </div>
        </div>
        <div
          className={`mt-[8px] min-h-[18px] px-[20px] text-[12px] text-muted transition-opacity duration-200 sm:px-0 ${
            limitsReady &&
            (BigNumber(currentMin).isGreaterThan(0) || BigNumber(currentMax).isGreaterThan(0))
              ? 'opacity-100'
              : 'opacity-0'
          }`}
          aria-hidden={!limitsReady}
        >
          {limitsReady && BigNumber(currentMin).isGreaterThan(0) && (
            <span>
              Min: {BigNumber(currentMin).toString()} {selectedToken}
            </span>
          )}
          {limitsReady &&
            BigNumber(currentMin).isGreaterThan(0) &&
            BigNumber(currentMax).isGreaterThan(0) && <span className="mx-[8px]">|</span>}
          {limitsReady && BigNumber(currentMax).isGreaterThan(0) && (
            <span>
              Max: {BigNumber(currentMax).toString()} {selectedToken}
            </span>
          )}
        </div>
        {hasAmountError && (
          <div className="mt-[4px] px-[20px] text-[12px] text-red-500 sm:px-0">
            {BigNumber(currentMin).isGreaterThan(0) && inputAmount.isLessThan(currentMin)
              ? `Amount must be at least ${BigNumber(currentMin).toString()} ${selectedToken}`
              : `Amount must not exceed ${BigNumber(currentMax).toString()} ${selectedToken}`}
          </div>
        )}
        {type === 'n3ToNeox' && (
          <button
            className={`mt-[18px] w-full rounded-[10px] bg-action-btn py-[12px] font-bold text-white shadow-box-shadow duration-200  ${
              n3DepositStatus === 'pending' ||
              BigNumber(maxTotalDeposit).minus(totalDeposit).isLessThan(1) ||
              isSubmitBlocked
                ? 'cursor-not-allowed opacity-25 shadow-none'
                : 'hover:opacity-75'
            }`}
            onClick={
              n3DepositStatus === 'pending' ||
              BigNumber(maxTotalDeposit).minus(totalDeposit).isLessThan(1) ||
              isSubmitBlocked
                ? undefined
                : deposit
            }
          >
            {BigNumber(maxTotalDeposit).minus(totalDeposit).isLessThan(1)
              ? 'Insufficient Quota'
              : 'Deposit'}
          </button>
        )}
        {type === 'neoxToN3' && (
          <button
            className={`mt-[18px] w-full rounded-[10px] bg-action-btn py-[12px] font-bold text-white shadow-box-shadow duration-200 ${
              neoxWithdrawStatus === 'pending' || isSubmitBlocked
                ? 'cursor-not-allowed opacity-25 shadow-none'
                : 'hover:opacity-75'
            }`}
            onClick={neoxWithdrawStatus === 'pending' || isSubmitBlocked ? undefined : withdraw}
          >
            Withdraw
          </button>
        )}
      </div>
      {type === 'n3ToNeox' && (
        <div className="z-1 relative mx-auto w-[600px] px-[28px] sm:w-full sm:px-0 sm:text-[12px]">
          {n3DepositStatus !== 'default' && (
            <div className="mt-[20px] flex items-center gap-[20px] sm:gap-[16px]">
              {n3DepositStatus === 'pending' && (
                <div className="flex items-center gap-[15px] sm:gap-[10px]">
                  <Loading className="animate-spin text-primary sm:w-[20px]" />
                  <div className="font-medium text-text/65">Depositing</div>
                </div>
              )}
              {n3DepositStatus === 'success' && (
                <div className="flex items-center gap-[15px] sm:gap-[10px]">
                  <CheckCircleOutlined
                    rev={undefined}
                    style={{ fontSize: '20px', color: 'hsl(var(--primary))' }}
                  />
                  <div className="font-medium text-text/65">Deposited</div>
                </div>
              )}
              {n3DepositStatus === 'error' && (
                <div className="flex items-center gap-[15px] sm:gap-[10px]">
                  <CloseCircleOutlined style={{ fontSize: '20px', color: 'red' }} rev={undefined} />
                  <div className="font-medium text-text/65">Fault</div>
                </div>
              )}
              {n3DepositTxId && n3DepositStatus !== 'pending' && (
                <div className="flex items-center gap-[10px]">
                  <a
                    className="block"
                    href={`${NEO_BLOCK_EXPLORER}/transactionInfo/${n3DepositTxId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View on OneGate Explorer
                  </a>
                  <img src={jump} className="sm:w-[12px]" />
                </div>
              )}
            </div>
          )}
          {neoxDepositStatus !== 'default' && (
            <div className="mt-[20px] flex items-center gap-[20px] sm:gap-[16px]">
              {neoxDepositStatus === 'pending' && (
                <div className="flex items-center gap-[15px] sm:gap-[10px]">
                  <Loading className="animate-spin text-primary sm:w-[20px]" />
                  <div className="font-medium text-text/65">Transferring</div>
                </div>
              )}
              {neoxDepositStatus === 'success' && (
                <div className="flex items-center gap-[15px] sm:gap-[10px]">
                  <CheckCircleOutlined
                    rev={undefined}
                    style={{ fontSize: '20px', color: 'hsl(var(--primary))' }}
                  />
                  <div className="font-medium text-text/65">Transferred</div>
                </div>
              )}
              {neoxDepositStatus === 'error' && (
                <div className="flex items-center gap-[15px] sm:gap-[10px]">
                  <CloseCircleOutlined rev={undefined} style={{ fontSize: '20px', color: 'red' }} />
                  <div className="font-medium text-text/65">Fault</div>
                </div>
              )}
              {neoxDepositTxId && n3DepositStatus !== 'pending' && (
                <div className="flex items-center gap-[10px]">
                  <a
                    className="block"
                    href={`${NEOX_BLOCK_EXPLORER}/tx/${neoxDepositTxId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View on Neo X Explorer
                  </a>
                  <img src={jump} className="sm:w-[12px]" />
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {type === 'neoxToN3' && (
        <div className="z-1 relative mx-auto w-[600px] px-[28px] sm:w-full sm:px-0 sm:text-[12px]">
          {isApproving && neoxWithdrawStatus === 'default' && (
            <div className="mt-[20px] flex items-center gap-[15px] sm:gap-[10px]">
              <Loading className="animate-spin text-primary sm:w-[20px]" />
              <div className="font-medium text-text/65">Approve NeoX</div>
            </div>
          )}
          <div className="flex items-center gap-[20px] sm:gap-[16px]">
            {neoxWithdrawStatus === 'pending' && (
              <div className="mt-[20px] flex items-center gap-[15px] sm:gap-[10px]">
                <Loading className="animate-spin text-primary sm:w-[20px]" />
                <div className="font-medium text-text/65">Withdrawing</div>
              </div>
            )}
            {neoxWithdrawStatus === 'success' && (
              <div className="mt-[20px] flex items-center gap-[15px] sm:gap-[10px]">
                <CheckCircleOutlined
                  rev={undefined}
                  style={{ fontSize: '20px', color: 'hsl(var(--primary))' }}
                />
                <div className="font-medium text-text/65">Withdrawn</div>
              </div>
            )}
            {neoxWithdrawStatus === 'error' && (
              <div className="mt-[20px] flex items-center gap-[15px] sm:gap-[10px]">
                <CloseCircleOutlined style={{ fontSize: '20px', color: 'red' }} rev={undefined} />
                <div className="font-medium text-text/65">Fault</div>
              </div>
            )}
            {neoxWithdrawHash !== '' && (
              <div className="mt-[20px] flex items-center gap-[10px]">
                <a
                  className="block"
                  href={`${NEOX_BLOCK_EXPLORER}/tx/${neoxWithdrawHash}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View on Neo X Explorer
                </a>
                <img src={jump} className="sm:w-[12px]" />
              </div>
            )}
          </div>
          {neoxWithdrawStatus === 'success' && (
            <div className="mt-[20px] flex items-center gap-[20px] sm:gap-[16px]">
              {n3WithdrawStatus === 'pending' && (
                <div className="flex items-center gap-[15px] sm:gap-[10px]">
                  <Loading className="animate-spin text-primary sm:w-[20px]" />
                  <div className="font-medium text-text/65">Transferring</div>
                </div>
              )}
              {n3WithdrawStatus === 'success' && (
                <div className="flex items-center gap-[15px] sm:gap-[10px]">
                  <CheckCircleOutlined
                    rev={undefined}
                    style={{ fontSize: '20px', color: 'hsl(var(--primary))' }}
                  />
                  <div className="font-medium text-text/65">Transferred</div>
                </div>
              )}
              {n3WithdrawStatus === 'error' && (
                <div className="flex items-center gap-[15px] sm:gap-[10px]">
                  <CloseCircleOutlined rev={undefined} style={{ fontSize: '20px', color: 'red' }} />
                  <div className="font-medium text-text/65">Fault</div>
                </div>
              )}
              {n3WithdrawTxId !== '' && (
                <div className="flex items-center gap-[10px]">
                  <a
                    className="block"
                    href={`${NEO_BLOCK_EXPLORER}/transactionInfo/${n3WithdrawTxId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View on OneGate Explorer
                  </a>
                  <img src={jump} className="sm:w-[12px]" />
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <HistoryChart tokenMap={tokenMap} />
      <div className="h-[100px] w-full" />
    </div>
  );
};
